import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UsersIcon, 
  BuildingStorefrontIcon, 
  CalendarIcon, 
  CurrencyDollarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowRightOnRectangleIcon,
  UserPlusIcon,
  ShieldCheckIcon,
  EyeIcon,
  NoSymbolIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { adminAuthAPI } from '../../api/adminAuth';
import BusinessReviewModal from '../../components/admin/BusinessReviewModal';
import UserDetailsModal from '../../components/admin/UserDetailsModal';
import toast from 'react-hot-toast';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [stats, setStats] = useState(null);
  const [pendingBusinesses, setPendingBusinesses] = useState([]);
  const [pendingBusinessUpdates, setPendingBusinessUpdates] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [usersData, setUsersData] = useState({ users: [], total: 0, limit: 10, offset: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);

  const parseBusinessDetails = (details) => {
    if (!details) return {};
    if (typeof details === 'string') {
      try {
        return JSON.parse(details);
      } catch (e) {
        console.error('Failed to parse business_details in AdminDashboard', e);
        return {};
      }
    }
    return details;
  };

  useEffect(() => {
    checkAdminAuth();
  }, []);

  const checkAdminAuth = () => {
    const adminUser = localStorage.getItem('adminUser');
    const adminToken = localStorage.getItem('adminToken');
    
    console.log('Admin check - Token exists:', !!adminToken);
    console.log('Admin check - User exists:', !!adminUser);
    
    if (!adminToken || !adminUser) {
      console.log('No admin token, redirecting to login');
      navigate('/admin/login');
      return;
    }
    
    try {
      const parsedUser = JSON.parse(adminUser);
      setAdmin(parsedUser);
      loadDashboardData();
    } catch (err) {
      console.error('Failed to parse admin user:', err);
      navigate('/admin/login');
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading dashboard data...');
      
      // Test each API call individually to identify which one fails
      try {
        console.log('Fetching stats...');
        const statsRes = await adminAuthAPI.getAdminStats();
        console.log('Stats response:', statsRes.data);
        setStats(statsRes.data.data);
      } catch (statsErr) {
        console.error('Stats error:', statsErr);
        toast.error('Failed to load stats');
      }
      
      try {
        console.log('Fetching pending businesses...');
        const pendingRes = await adminAuthAPI.getPendingBusinesses();
        console.log('Pending response:', pendingRes.data);
        setPendingBusinesses(pendingRes.data.data?.businesses || []);
      } catch (pendingErr) {
        console.error('Pending businesses error:', pendingErr);
        // Don't show error for this, just set empty array
        setPendingBusinesses([]);
      }

      try {
        const updatesRes = await adminAuthAPI.getPendingBusinessChangeRequests();
        setPendingBusinessUpdates(updatesRes.data.data?.requests || []);
      } catch (updatesErr) {
        console.error('Pending business updates error:', updatesErr);
        setPendingBusinessUpdates([]);
      }
      
      try {
        console.log('Fetching users...');
        const usersRes = await adminAuthAPI.getAllUsers({ limit: 10, offset: 0 });
        console.log('Users response:', usersRes.data);
        const data = usersRes.data.data || { users: [], total: 0, limit: 10, offset: 0 };
        setRecentUsers(data.users || []);
        setUsersData(data);
      } catch (usersErr) {
        console.error('Users error:', usersErr);
        setRecentUsers([]);
        setUsersData({ users: [], total: 0, limit: 10, offset: 0 });
      }
      
    } catch (error) {
      console.error('Dashboard load error:', error);
      setError(error.message);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await adminAuthAPI.approveBusiness(userId);
      toast.success('Business approved successfully!');
      loadDashboardData();
      setShowReviewModal(false);
      setSelectedBusiness(null);
    } catch (error) {
      console.error('Approve error:', error);
      toast.error(error.response?.data?.message || 'Failed to approve business');
    }
  };

  const handleReject = async (userId) => {
    const reason = prompt('Enter rejection reason:');
    if (reason) {
      try {
        await adminAuthAPI.rejectBusiness(userId, reason);
        toast.success('Business rejected');
        loadDashboardData();
        setShowReviewModal(false);
        setSelectedBusiness(null);
      } catch (error) {
        console.error('Reject error:', error);
        toast.error(error.response?.data?.message || 'Failed to reject business');
      }
    }
  };

  const handleApproveUpdate = async (requestId) => {
    try {
      await adminAuthAPI.approveBusinessChangeRequest(requestId);
      toast.success('Business changes approved');
      loadDashboardData();
    } catch (error) {
      console.error('Approve update error:', error);
      toast.error(error.response?.data?.message || 'Failed to approve business changes');
    }
  };

  const handleRejectUpdate = async (requestId) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      await adminAuthAPI.rejectBusinessChangeRequest(requestId, reason);
      toast.success('Business changes rejected');
      loadDashboardData();
    } catch (error) {
      console.error('Reject update error:', error);
      toast.error(error.response?.data?.message || 'Failed to reject business changes');
    }
  };

  const handleBlock = async (userId) => {
    const confirmed = window.confirm('Block this user? This will disable their account. For business owners, their business will also be deactivated.');
    if (!confirmed) return;

    try {
      await adminAuthAPI.blockUser(userId);
      toast.success('User blocked');
      loadDashboardData();
      setShowReviewModal(false);
      setSelectedBusiness(null);
    } catch (error) {
      console.error('Block error:', error);
      toast.error(error.response?.data?.message || 'Failed to block user');
    }
  };

  const handleDelete = async (userId) => {
    const confirmed = window.confirm('Delete this user permanently? This cannot be undone.');
    if (!confirmed) return;

    try {
      await adminAuthAPI.deleteUser(userId);
      toast.success('User deleted');
      loadDashboardData();
      setShowReviewModal(false);
      setSelectedBusiness(null);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleViewDetails = (business) => {
    console.log('View Details clicked for business:', business);
    setSelectedBusiness(business);
    setShowReviewModal(true);
  };

  const handleViewUserDetails = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const loadUsers = async (offset = 0) => {
    try {
      const usersRes = await adminAuthAPI.getAllUsers({ limit: 10, offset });
      const data = usersRes.data.data || { users: [], total: 0, limit: 10, offset: 0 };
      setRecentUsers(data.users || []);
      setUsersData(data);
    } catch (usersErr) {
      console.error('Users error:', usersErr);
      setRecentUsers([]);
      setUsersData({ users: [], total: 0, limit: 10, offset: 0 });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-red-900/50 border border-red-500 rounded-lg p-6 max-w-md text-center">
          <h2 className="text-xl font-bold text-red-400 mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-300 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const statCards = [
    { title: 'Total Users', value: stats?.total_users || 0, icon: UsersIcon, color: 'bg-blue-500' },
    { title: 'Pending Approvals', value: stats?.pending_businesses || 0, icon: ClockIcon, color: 'bg-yellow-500' },
    { title: 'Total Bookings', value: stats?.total_bookings || 0, icon: CalendarIcon, color: 'bg-green-500' },
    { title: 'Total Revenue', value: `$${stats?.total_revenue || 0}`, icon: CurrencyDollarIcon, color: 'bg-purple-500' }
  ];

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Admin Header */}
      <nav className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <ShieldCheckIcon className="h-8 w-8 text-primary-500" />
              <span className="ml-2 text-xl font-bold text-white">BookFlow Admin</span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-white">{admin?.first_name} {admin?.last_name}</p>
                <p className="text-xs text-gray-400">Super Administrator</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5 mr-1" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
          <p className="text-gray-400">Welcome back, {admin?.first_name}! Here's what's happening today.</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => (
            <div key={index} className="bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="flex items-center">
                <div className={`p-3 rounded-full ${stat.color} bg-opacity-10`}>
                  <stat.icon className={`h-6 w-6 ${stat.color.replace('bg-', 'text-')}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-400">{stat.title}</p>
                  <p className="text-2xl font-bold text-white">{stat.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-700 mb-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'overview'
                  ? 'border-primary-500 text-primary-500'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`py-4 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'pending'
                  ? 'border-primary-500 text-primary-500'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              Pending Approvals
              {pendingBusinesses.length > 0 && (
                <span className="ml-2 bg-yellow-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {pendingBusinesses.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('updates')}
              className={`py-4 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'updates'
                  ? 'border-primary-500 text-primary-500'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              Business Updates
              {pendingBusinessUpdates.length > 0 && (
                <span className="ml-2 bg-yellow-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {pendingBusinessUpdates.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`py-4 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'users'
                  ? 'border-primary-500 text-primary-500'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              All Users
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="bg-gray-800 rounded-lg shadow-lg">
              <div className="p-6 border-b border-gray-700">
                <h2 className="text-lg font-semibold text-white">Recent Registrations</h2>
              </div>
              <div className="overflow-x-auto">
                {recentUsers.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">No users found</div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Registered</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {recentUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-700">
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-sm font-medium text-white">{user.first_name} {user.last_name}</p>
                              <p className="text-sm text-gray-400">{user.email}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`text-sm capitalize ${user.user_type === 'business_owner' ? 'text-yellow-400' : 'text-blue-400'}`}>
                              {user.user_type}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`text-sm capitalize ${
                              user.approval_status === 'approved' ? 'text-green-400' : 
                              user.approval_status === 'pending' ? 'text-yellow-400' : 'text-red-400'
                            }`}>
                              {user.approval_status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-400">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleViewUserDetails(user)}
                                className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                              >
                                <EyeIcon className="h-4 w-4 mr-1" />
                                View Details
                              </button>
                              {!((user.user_type === 'super_admin' || user.id === admin?.id) && user.is_active !== false) && (
                                <button
                                  onClick={() => handleBlock(user.id)}
                                  className="inline-flex items-center px-3 py-1.5 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition text-sm"
                                >
                                  <NoSymbolIcon className="h-4 w-4 mr-1" />
                                  Block
                                </button>
                              )}
                              {!(user.user_type === 'super_admin' || user.id === admin?.id) && (
                                <button
                                  onClick={() => handleDelete(user.id)}
                                  className="inline-flex items-center px-3 py-1.5 bg-gray-900 text-white rounded-lg hover:bg-black transition text-sm"
                                >
                                  <TrashIcon className="h-4 w-4 mr-1" />
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'pending' && (
          <div className="bg-gray-800 rounded-lg shadow-lg">
            {pendingBusinesses.length === 0 ? (
              <div className="p-12 text-center">
                <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <p className="text-gray-400">No pending approvals. All businesses are approved!</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-700">
                {pendingBusinesses.map((business) => {
                  const details = parseBusinessDetails(business.business_details);
                  return (
                    <div key={business.id} className="p-6 hover:bg-gray-700">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <BuildingStorefrontIcon className="h-5 w-5 text-gray-400 mr-2" />
                            <h3 className="text-lg font-semibold text-white">
                              {details.business_name || 'Business Name Pending'}
                            </h3>
                          </div>
                          <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                            <div>
                              <p className="text-gray-400">Owner</p>
                              <p className="text-white">{business.first_name} {business.last_name}</p>
                            </div>
                            <div>
                              <p className="text-gray-400">Email</p>
                              <p className="text-white">{business.email}</p>
                            </div>
                            <div>
                              <p className="text-gray-400">Business Type</p>
                              <p className="text-white capitalize">{details.business_type}</p>
                            </div>
                            <div>
                              <p className="text-gray-400">Registered</p>
                              <p className="text-white">{new Date(business.created_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <div className="mt-3">
                            <p className="text-gray-400 text-sm">Address</p>
                            <p className="text-sm text-white">{details.business_address}</p>
                          </div>
                        </div>
                        <div className="flex flex-col space-y-2 ml-4">
                          <button
                            onClick={() => handleViewDetails(business)}
                            className="flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-md transition"
                          >
                            <EyeIcon className="h-5 w-5 mr-2" />
                            View Details
                          </button>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleApprove(business.id)}
                              className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                            >
                              <CheckCircleIcon className="h-5 w-5 mr-1" />
                              Approve
                            </button>
                            <button
                              onClick={() => handleReject(business.id)}
                              className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                            >
                              <XCircleIcon className="h-5 w-5 mr-1" />
                              Reject
                            </button>
                            <button
                              onClick={() => handleBlock(business.id)}
                              className="flex items-center px-3 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition"
                            >
                              <NoSymbolIcon className="h-5 w-5 mr-1" />
                              Block
                            </button>
                            <button
                              onClick={() => handleDelete(business.id)}
                              className="flex items-center px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-black transition"
                            >
                              <TrashIcon className="h-5 w-5 mr-1" />
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'updates' && (
          <div className="bg-gray-800 rounded-lg shadow-lg">
            {pendingBusinessUpdates.length === 0 ? (
              <div className="p-12 text-center">
                <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <p className="text-gray-400">No pending business updates.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-700">
                {pendingBusinessUpdates.map((req) => {
                  const changeKeys = Object.keys(req?.changes || {});
                  return (
                    <div key={req.id} className="p-6 hover:bg-gray-700">
                      <div className="flex justify-between items-start gap-6">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <BuildingStorefrontIcon className="h-5 w-5 text-gray-400 mr-2" />
                            <h3 className="text-lg font-semibold text-white">{req.business_name}</h3>
                          </div>
                          <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                            <div>
                              <p className="text-gray-400">Owner</p>
                              <p className="text-white">
                                {req.owner_first_name} {req.owner_last_name}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-400">Owner Email</p>
                              <p className="text-white">{req.owner_email}</p>
                            </div>
                            <div>
                              <p className="text-gray-400">Business Type</p>
                              <p className="text-white capitalize">{req.business_type}</p>
                            </div>
                            <div>
                              <p className="text-gray-400">Submitted</p>
                              <p className="text-white">{new Date(req.created_at).toLocaleString()}</p>
                            </div>
                          </div>
                          <div className="mt-3">
                            <p className="text-gray-400 text-sm">Changed Fields</p>
                            <p className="text-sm text-white">{changeKeys.length ? changeKeys.join(', ') : '—'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleApproveUpdate(req.id)}
                            className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                          >
                            <CheckCircleIcon className="h-5 w-5 mr-1" />
                            Approve
                          </button>
                          <button
                            onClick={() => handleRejectUpdate(req.id)}
                            className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                          >
                            <XCircleIcon className="h-5 w-5 mr-1" />
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="bg-gray-800 rounded-lg shadow-lg">
            <div className="p-6 border-b border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-white">All Platform Users</h2>
                <button className="flex items-center px-3 py-1 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                  <UserPlusIcon className="h-5 w-5 mr-1" />
                  Export Data
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              {recentUsers.length === 0 ? (
                <div className="p-8 text-center text-gray-400">No users found</div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Bookings</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Joined</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {recentUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-700">
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-sm font-medium text-white">{user.first_name} {user.last_name}</p>
                            <p className="text-sm text-gray-400">{user.email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-sm capitalize ${
                            user.user_type === 'business_owner' ? 'text-yellow-400' : 
                            user.user_type === 'super_admin' ? 'text-purple-400' : 'text-blue-400'
                          }`}>
                            {user.user_type}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-sm capitalize ${
                            user.is_active ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-400">0</td>
                        <td className="px-6 py-4 text-sm text-gray-400">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewUserDetails(user)}
                              className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                            >
                              <EyeIcon className="h-4 w-4 mr-1" />
                              View Details
                            </button>
                            {!((user.user_type === 'super_admin' || user.id === admin?.id) && user.is_active !== false) && (
                              <button
                                onClick={() => handleBlock(user.id)}
                                className="inline-flex items-center px-3 py-1.5 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition text-sm"
                              >
                                <NoSymbolIcon className="h-4 w-4 mr-1" />
                                Block
                              </button>
                            )}
                            {!(user.user_type === 'super_admin' || user.id === admin?.id) && (
                              <button
                                onClick={() => handleDelete(user.id)}
                                className="inline-flex items-center px-3 py-1.5 bg-gray-900 text-white rounded-lg hover:bg-black transition text-sm"
                              >
                                <TrashIcon className="h-4 w-4 mr-1" />
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {usersData.total > usersData.limit && (
              <div className="p-6 border-t border-gray-700 flex justify-between items-center">
                <p className="text-sm text-gray-400">
                  Showing {usersData.offset + 1} to {Math.min(usersData.offset + usersData.limit, usersData.total)} of {usersData.total} users
                </p>
                <div className="flex items-center gap-2">
                  <button
                    disabled={usersData.offset === 0}
                    onClick={() => loadUsers(Math.max(0, usersData.offset - usersData.limit))}
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    Previous
                  </button>
                  <button
                    disabled={usersData.offset + usersData.limit >= usersData.total}
                    onClick={() => loadUsers(usersData.offset + usersData.limit)}
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Business Review Modal */}
      {showReviewModal && selectedBusiness && (
        <BusinessReviewModal
          business={selectedBusiness}
          onClose={() => {
            setShowReviewModal(false);
            setSelectedBusiness(null);
          }}
          onApprove={handleApprove}
          onReject={handleReject}
          onBlock={handleBlock}
          onDelete={handleDelete}
        />
      )}

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          onClose={() => {
            setShowUserModal(false);
            setSelectedUser(null);
          }}
          onRefresh={loadDashboardData}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
